/*
   The engagements a review can be filed against.
*/

import { queryOptions } from "@tanstack/react-query"

import { http } from "@/lib/http"
import { MAX_PAGE_SIZE } from "@/lib/pagination"
import { qk } from "@/lib/query-keys"
import type { components } from "@/types/api.gen"

type ClientPage = components["schemas"]["Page_ClientOut_"]

/** An engagement, reduced to what a picker needs. */
export interface EngagementOption {
  id: number
  name: string
}

/**
 * Engagements for the intake picker.
 *
 * `enabled` exists because /clients is admin-only: asking as anyone else buys a
 * 403 the sheet would have to explain. Filing against no engagement is valid —
 * the review is then internal — so an absent picker costs the reviewer nothing.
 *
 * One page at the server's maximum, deliberately unpaged: a combobox over more
 * engagements than that needs server-side search, which is a different control
 * than this one.
 */
export const engagementsQuery = (enabled: boolean) =>
  queryOptions({
    queryKey: qk.clients.list({ forIntake: true }),
    queryFn: async ({ signal }) => {
      const page = await http.get<ClientPage>("/api/v1/clients", {
        params: { page: 1, page_size: MAX_PAGE_SIZE },
        signal,
      })
      return page.items.map((c): EngagementOption => ({ id: c.id, name: c.name }))
    },
    enabled,
    staleTime: 5 * 60_000,
  })
